/**
 * Staff display-name helpers.
 *
 * Backend `/api/organisation/mem` returns `StaffMemberResponse` rows with
 * `first_name`, `last_name`, `username`, `role`, `specialization`. Most
 * pickers in the app used to fall back to `first_name` alone, which made
 * doctors with the same first name impossible to distinguish (e.g. two
 * "Priya"s on staff).
 *
 * `formatStaffName(member)` returns the cleanest full identifier we can
 * construct from those fields, falling back gracefully:
 *
 *   "First Last"             — the common case
 *   "First"                  — only first_name set
 *   "Last"                   — only last_name set (rare)
 *   member.username          — when no name fields are populated
 *   member.member_id         — final fallback
 *
 * `formatDoctorName(member)` returns the same with a "Dr. " prefix for
 * doctor rows, suitable for "this plan is for: Dr. ___" labels.
 *
 * `formatStaffNameWithRoleHint(member)` appends a short specialization
 * or role tag in parens — useful in dropdowns where two staff members
 * share a name. Returns plain `formatStaffName` when no hint is available.
 */

export function formatStaffName(member) {
  if (!member || typeof member !== 'object') return '';
  const first = (member.first_name || '').trim();
  const last = (member.last_name || '').trim();
  const full = [first, last].filter(Boolean).join(' ').trim();
  return full || member.username || member.name || member.member_id || '';
}

export function formatDoctorName(member) {
  const base = formatStaffName(member);
  if (!base) return '';
  return base.startsWith('Dr.') ? base : `Dr. ${base}`;
}

export function formatStaffNameWithRoleHint(member) {
  const base = formatStaffName(member);
  if (!base) return '';
  const role = (member?.role || '').toUpperCase();
  const spec = (member?.specialization || '').trim();
  // Doctors: prefer specialization as the disambiguator; otherwise show
  // the role tag for non-doctors so e.g. "Priya · NURSE" stands apart
  // from "Priya · DOCTOR".
  if (role === 'DOCTOR' && spec) return `${base} · ${spec}`;
  if (role && role !== 'DOCTOR') return `${base} · ${role}`;
  return base;
}
